// Copied verbatim from the desktop app so the two look identical.
// Do not edit here: change the app's copy, then run
//   node scripts/sync-app-components.mjs --write
// `pnpm verify:components` fails the build if the two drift apart.
import type { ReactNode } from "react";
import { toast as sonnerToast, type ExternalToast } from "sonner";

type ToastMessage = string | ReactNode;

let repopFlip = false;

/**
 * Stable id so a repeat of the same toast updates the existing one in place
 * (never two elements at once). When the toast is still visible, alternate
 * between two identical pop keyframes — the animation-name change restarts
 * the CSS animation, replaying the pop. Explicit `data.id` always wins.
 */
const toastOptions = (
  kind: string,
  message: ToastMessage,
  data?: ExternalToast
): ExternalToast | undefined => {
  if (data?.id != null) return data;
  if (typeof message !== "string" || !message) return data;
  const id = `${kind}:${message}`;
  const options: ExternalToast = { ...data, id };
  if (sonnerToast.getToasts().some((t) => t.id === id)) {
    repopFlip = !repopFlip;
    const repop = repopFlip ? "toast-repop-a" : "toast-repop-b";
    options.className = data?.className ? `${data.className} ${repop}` : repop;
  }
  return options;
};

/** Sonner wrapper — same API, but a repeat of an identical toast replays a pop on the existing one. */
export const toast = Object.assign(
  (message: ToastMessage, data?: ExternalToast) =>
    sonnerToast(message, toastOptions("default", message, data)),
  {
    success: (message: ToastMessage, data?: ExternalToast) =>
      sonnerToast.success(message, toastOptions("success", message, data)),
    error: (message: ToastMessage, data?: ExternalToast) =>
      sonnerToast.error(message, toastOptions("error", message, data)),
    info: (message: ToastMessage, data?: ExternalToast) =>
      sonnerToast.info(message, toastOptions("info", message, data)),
    warning: (message: ToastMessage, data?: ExternalToast) =>
      sonnerToast.warning(message, toastOptions("warning", message, data)),
    message: (message: ToastMessage, data?: ExternalToast) =>
      sonnerToast.message(message, toastOptions("message", message, data)),
    loading: (message: ToastMessage, data?: ExternalToast) =>
      sonnerToast.loading(message, toastOptions("loading", message, data)),
    promise: sonnerToast.promise.bind(sonnerToast),
    custom: sonnerToast.custom.bind(sonnerToast),
    dismiss: sonnerToast.dismiss.bind(sonnerToast),
    getHistory: sonnerToast.getHistory.bind(sonnerToast),
    getToasts: sonnerToast.getToasts.bind(sonnerToast)
  }
);
